import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Language, translations } from '../thi-translations';
import { normalizeThiLang } from '../thi-lang.util';
import { getCampaignT } from './thi-campaign-translations';
import { AuthService } from 'src/app/Auth/service/auth.service';
import { LanguageService } from 'src/app/shared/services/language.service';
import { ThiCampaignAttributionService } from './thi-campaign-attribution.service';
import { ThiLandingAnalyticsService } from './thi-landing-analytics.service';

@Component({
  selector: 'app-thi-campaign-header',
  templateUrl: './thi-campaign-header.component.html',
  styleUrls: ['../thi-landing-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThiCampaignHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() lang: Language = 'ar';
  @Output() scrolledPastHero = new EventEmitter<boolean>();
  @Output() langChange = new EventEmitter<Language>();

  t = translations.ar;
  c = getCampaignT('ar');
  dir: 'rtl' | 'ltr' = 'rtl';
  isMenuOpen = false;
  showLangMenu = false;
  isScrolled = false;
  loggedIn = false;

  readonly languages: { code: Language; label: string }[] = [
    { code: 'ar', label: 'AR' },
    { code: 'en', label: 'EN' },
    { code: 'he', label: 'HE' },
  ];

  private readonly destroy$ = new Subject<void>();
  private scrollListener!: () => void;
  private clickListener!: (e: MouseEvent) => void;

  constructor(
    public router: Router,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private languageService: LanguageService,
    private attribution: ThiCampaignAttributionService,
    private analytics: ThiLandingAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.applyLang();
    this.loggedIn = !!localStorage.getItem('token');
    this.authService.isLogin.pipe(takeUntil(this.destroy$)).subscribe((v) => {
      this.loggedIn = !!v;
      this.cdr.markForCheck();
    });

    this.ngZone.runOutsideAngular(() => {
      this.scrollListener = (): void => {
        const y = window.scrollY;
        const nextScrolled = y > 20;
        const pastHero = y > 80;
        if (nextScrolled !== this.isScrolled) {
          this.ngZone.run(() => {
            this.isScrolled = nextScrolled;
            this.cdr.markForCheck();
          });
        }
        this.ngZone.run(() => this.scrolledPastHero.emit(pastHero));
      };

      this.clickListener = (e: MouseEvent): void => {
        if (!this.showLangMenu) {
          return;
        }
        const el = e.target as HTMLElement | null;
        if (!el?.closest('#thi-campaign-lang-trigger') && !el?.closest('#thi-campaign-lang-listbox')) {
          this.ngZone.run(() => {
            this.showLangMenu = false;
            this.cdr.markForCheck();
          });
        }
      };

      window.addEventListener('scroll', this.scrollListener, { passive: true });
      document.addEventListener('click', this.clickListener as EventListener, true);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('scroll', this.scrollListener);
    document.removeEventListener('click', this.clickListener as EventListener, true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lang']) {
      this.applyLang();
      this.cdr.markForCheck();
    }
  }

  get headerSurfaceClass(): string {
    return this.isScrolled
      ? 'bg-card/95 backdrop-blur-xl shadow-lg border-b border-border'
      : 'bg-card/95 backdrop-blur-xl';
  }

  get registerParams(): Record<string, string> {
    return this.attribution.registerQueryParams();
  }

  get accountDashboardLink(): string[] {
    const raw = localStorage.getItem('user_type');
    let ut: number | undefined;
    try {
      ut = raw != null ? JSON.parse(raw) : undefined;
    } catch {
      ut = undefined;
    }
    if (ut === 100 || ut === 101) {
      return ['/dashboard', 'dashboard'];
    }
    if (ut === 300 || ut === 301) {
      return ['/dashboard', 'office-companies'];
    }
    return ['/dashboard', 'dashboard'];
  }

  scrollTo(fragment: string): void {
    this.isMenuOpen = false;
    const id = fragment.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  onStartFree(section: string): void {
    this.analytics.startFreeClick(this.lang, section);
  }

  changeLang(code: Language): void {
    this.showLangMenu = false;
    this.isMenuOpen = false;
    this.languageService.apply(code);
    this.lang = code;
    this.applyLang();
    this.langChange.emit(code);
    this.cdr.markForCheck();
  }

  toggleLangMenu(event: Event): void {
    event.stopPropagation();
    this.showLangMenu = !this.showLangMenu;
    this.cdr.markForCheck();
  }

  private applyLang(): void {
    this.lang = normalizeThiLang(this.lang as unknown as string);
    this.t = translations[this.lang];
    this.c = getCampaignT(this.lang);
    this.dir = this.lang === 'en' ? 'ltr' : 'rtl';
  }
}
